import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SourceAttributionBadge } from '@/components/catalog/source-attribution-badge'
import { AffiliateDisclosureFooter } from '@/components/checkout/affiliate-disclosure-footer'
import { formatUsd } from '@/features/projects/rollup-bom'
import type { CartGroup, CartItem } from '@/features/checkout/cart'

interface CartPreviewDialogProps {
  group: CartGroup
  open: boolean
  onOpenChange: (open: boolean) => void
}

const formatQty = (qty: number, unit: string | null): string => {
  const nice = Number.isInteger(qty) ? String(qty) : qty.toFixed(2).replace(/\.?0+$/, '')
  return unit ? `× ${nice} ${unit}` : `× ${nice}`
}

const itemHref = (item: CartItem): string => item.affiliateUrl ?? item.sourceUrl

interface CartItemRowProps {
  item: CartItem
}

const CartItemRow = ({ item }: CartItemRowProps) => (
  <li className="flex items-center justify-between gap-3 py-2 text-sm">
    <a
      href={itemHref(item)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-1 items-center gap-2 truncate hover:underline"
      title={`Open ${item.name} at ${item.sourceDisplayName} in a new tab`}
    >
      <span className="truncate">{item.name}</span>
      <SourceAttributionBadge
        tier={item.priceTier}
        liveSource={null}
        isStale={item.isLivePriceStale}
      />
      <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
    </a>
    <span className="text-xs text-muted-foreground">
      {formatQty(item.quantity, item.unit)} × {formatUsd(item.unitPriceCents)}
    </span>
    <span className="w-20 text-right tabular-nums">
      {formatUsd(Math.round(item.quantity * item.unitPriceCents))}
    </span>
  </li>
)

/**
 * Phase 17 — modal listing every item in a Project's BOM that routes to a single Source. The
 * upstream caller (a BOM panel) feeds in a {@link CartGroup} pre-filtered + grouped by {@code
 * groupCartItemsBySource}; this component is a pure renderer.
 *
 * <p>Click targets prefer {@code affiliateUrl} (Phase 17 injection) over the bare {@code
 * sourceUrl}. A tooltipped "Open all in new tabs" button lives at the bottom; modern browsers
 * gate {@code window.open} outside of direct user gestures, so multi-tab opens may be blocked
 * as popups — the per-row links are the canonical fallback.
 *
 * <p>The disclosure footer renders only when at least one item carries an affiliate link
 * (driven by {@code group.hasAnyAffiliateLink}). Cart with all bare {@code sourceUrl}s has no
 * affiliate relationship to disclose.
 */
export const CartPreviewDialog = ({ group, open, onOpenChange }: CartPreviewDialogProps) => {
  const [popupHintShown, setPopupHintShown] = useState(false)

  const handleOpenAll = () => {
    if (!popupHintShown) setPopupHintShown(true)
    for (const item of group.items) {
      window.open(itemHref(item), '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Cart at {group.sourceDisplayName} · {group.items.length}{' '}
            {group.items.length === 1 ? 'item' : 'items'} · ~{formatUsd(group.subtotalCents)}
          </DialogTitle>
          <DialogDescription>
            Click an item to open it at {group.sourceDisplayName} in a new tab. Final price at the
            retailer is authoritative — this is a snapshot.
          </DialogDescription>
        </DialogHeader>

        {group.hasStaleLivePrice && (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            Some prices may be outdated. Refresh sources for current pricing.
          </div>
        )}

        {group.items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nothing to order — looks like you&apos;ve already got everything for this source.
          </p>
        ) : (
          <ul className="max-h-[50vh] divide-y overflow-y-auto">
            {group.items.map((item) => (
              <CartItemRow key={`${item.kind}:${item.catalogId}`} item={item} />
            ))}
          </ul>
        )}

        {group.hasAnyAffiliateLink && <AffiliateDisclosureFooter />}

        <DialogFooter className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          {group.items.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenAll}
              title={
                popupHintShown
                  ? 'Some browsers block opening multiple tabs at once'
                  : `Open all ${group.items.length} items in new tabs`
              }
            >
              Open all in new tabs
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
